import { Link, createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { LoaderCircle, ShieldCheck } from 'lucide-react'
import { z } from 'zod'
import { resolveAdminConversationImpersonation } from '@rpc/admin-impersonation.rpc'
import { authClient } from '@client/auth-client'

const searchSchema = z.object({
  conversationId: z.string().min(1),
  returnTo: z.string().optional(),
})

export const Route = createFileRoute('/admin/impersonate-conversation')({
  validateSearch: (search) => searchSchema.parse(search),
  loaderDeps: ({ search }) => ({
    conversationId: search.conversationId,
    returnTo: search.returnTo,
  }),
  loader: async ({ deps }) =>
    resolveAdminConversationImpersonation({ data: deps }),
  component: AdminConversationImpersonationHandoff,
})

function AdminConversationImpersonationHandoff() {
  const handoff = Route.useLoaderData()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (handoff.status !== 'ready') return

    let cancelled = false
    async function runHandoff() {
      try {
        const result = await authClient.admin.impersonateUser({
          userId: handoff.targetUserId,
        })
        if (cancelled) return
        if (result.error) {
          throw new Error(result.error.message || 'Impersonation failed')
        }
        window.location.assign(handoff.returnTo)
      } catch (err) {
        if (cancelled) return
        setError(
          err instanceof Error
            ? err.message
            : 'Impersonation failed. Open the read-only transcript instead.',
        )
      }
    }

    void runHandoff()
    return () => {
      cancelled = true
    }
  }, [handoff])

  const fallbackLink = (
    <Link
      to="/admin/conversations"
      search={{ conversationId: handoff.conversationId }}
      className="font-medium text-neutral-950 underline underline-offset-4 hover:text-neutral-700 dark:text-neutral-50 dark:hover:text-neutral-200"
    >
      Open read-only transcript
    </Link>
  )

  if (handoff.status === 'fallback') {
    return (
      <main className="mx-auto flex min-h-[50vh] w-full max-w-2xl flex-col justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="space-y-4 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm dark:border-neutral-800 dark:bg-neutral-950">
          <ShieldCheck className="h-6 w-6 text-neutral-500" aria-hidden />
          <h1 className="text-xl font-semibold text-neutral-950 dark:text-neutral-50">
            Impersonation unavailable
          </h1>
          <p className="text-sm leading-6 text-neutral-700 dark:text-neutral-300">
            {handoff.message}
          </p>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            {fallbackLink}
          </p>
        </div>
      </main>
    )
  }

  return (
    <main className="mx-auto flex min-h-[50vh] w-full max-w-2xl flex-col justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="space-y-4 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm dark:border-neutral-800 dark:bg-neutral-950">
        <LoaderCircle
          className="h-6 w-6 animate-spin text-neutral-500"
          aria-hidden
        />
        <h1 className="text-xl font-semibold text-neutral-950 dark:text-neutral-50">
          Opening customer conversation
        </h1>
        <p className="text-sm leading-6 text-neutral-700 dark:text-neutral-300">
          Starting a temporary impersonation session for{' '}
          {handoff.targetUserEmail || handoff.targetUserName || 'this user'}.
        </p>
        {error ? (
          <p className="text-sm leading-6 text-red-700 dark:text-red-300">
            {error} {fallbackLink}
          </p>
        ) : null}
      </div>
    </main>
  )
}
